import i18next from 'i18next';
import type { WeekNumbers } from 'luxon';
import { DateTime } from 'luxon';
import { zones } from 'tzdata';

type DayMonthFormat = 'LL/dd' | 'dd/LL';
type DateFormat = 'yyyy/LL/dd' | 'dd/LL/yyyy';

export type DayOfWeek = {
  value: string;
  label: string;
  numericDay: number;
};

const DEFAULT_TIME = '00:00';

export const getDayMonthDateFormat = (): string => {
  const language = i18next.language;
  switch (language) {
    case 'eu':
    case 'en_US':
    case 'en':
      return 'LL/dd';
    default:
      return 'dd/LL';
  }
};

export const getDateFormat = (): DateFormat => {
  const language = i18next.language;
  switch (language) {
    case 'eu':
    case 'en_US':
    case 'en':
      return 'yyyy/LL/dd';
    default:
      return 'dd/LL/yyyy';
  }
};

export const getWeekStartDay = () => {
  const language = i18next.language;
  switch (language) {
    case 'eu':
    case 'en_US':
    case 'en':
      return 0;
    default:
      return 1;
  }
};

export const getFormattedDate = (
  date: string | null | undefined | Date,
  format?: DateFormat
) => {
  if (!date || date === 'Invalid Date') return '';
  let ISODate = '';
  if (date instanceof Date) {
    if (isNaN(date.getTime())) return '';
    ISODate = date.toISOString();
  } else {
    ISODate = date;
  }
  return DateTime.fromISO(ISODate).toFormat(format || getDateFormat());
};

export const getFormattedDayMonthDate = (
  date: string | null | undefined,
  format?: DayMonthFormat
) => {
  if (!date) return '';
  return DateTime.fromISO(date).toFormat(format || getDayMonthDateFormat());
};

export const formatDateFromSql = (
  date: string | null | undefined,
  format?: DateFormat
) => {
  if (!date) return '';
  return DateTime.fromSQL(date).toFormat(format || getDateFormat());
};

const daysAgo = (date: string) => {
  const now = DateTime.local();
  const past = DateTime.fromSQL(date);
  const diff = now.diff(past, 'day');
  return Math.floor(diff.days);
};

export const nextYear = (date: string): string | null => {
  return DateTime.fromSQL(date).plus({ years: 1 }).toISODate();
};

export const getTimeAgo = (date: string | null | undefined) => {
  if (!date) return i18next.t('common.timeAgo.never');

  const days = daysAgo(date);

  if (days === 0) return i18next.t('common.timeAgo.today');
  if (days === 1) return i18next.t('common.timeAgo.yesterday');
  if (days >= 365) return i18next.t('common.timeAgo.yearAgo');
  if (days >= 30) return i18next.t('common.timeAgo.monthAgo');
  return DateTime.fromSQL(date).toFormat('dd/LL/yyyy');
};

// format timestamp ISO 8601 - YYYY-MM-DDTHH:mm:ss+/-hh:mm to dd/mm/yyyy hh:mm
export const getFormattedTimeStamp = (timestamp: string) => {
  const dateObj = new Date(timestamp);

  const day = dateObj.getUTCDate();
  const month = dateObj.getUTCMonth() + 1;
  const year = dateObj.getUTCFullYear();

  const hours = dateObj.getUTCHours();
  const minutes = dateObj.getUTCMinutes();

  const formattedDate = `${day}/${month}/${year}`;
  const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')}h`;

  return `${formattedDate}. ${formattedTime}`;
};

export const getNextSunday = (): Date => {
  const now = DateTime.now();
  const sunday = now.startOf('week').plus({ days: 7 });
  return sunday.toJSDate();
};

export const getPrevSunday = (): Date => {
  const now = DateTime.now();
  const sunday = now.startOf('week');
  return sunday.toJSDate();
};

export const getAllTimeZones = (): string[] => {
  return Object.entries(zones)
    .filter(([, v]) => Array.isArray(v))
    .map(([zoneName]) => zoneName)
    .filter((tz) => DateTime.local().setZone(tz).isValid);
};

export const getAllTimeZonesWithGMT = (): {
  timeZone: string;
  gmtOffset: string | undefined;
}[] => {
  const timeZones = getAllTimeZones();

  const exampleDate = new Date();

  function getGMTOffset(timeZone: string): string | undefined {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      hour12: false,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(exampleDate);
    const timeZonePart = parts?.find(
      (part) => part.type === 'timeZoneName'
    )?.value;
    return timeZonePart;
  }

  const timeZoneOffsets = timeZones.map((timeZone) => ({
    timeZone,
    gmtOffset: getGMTOffset(timeZone),
  }));

  return timeZoneOffsets;
};

export const getDayHours = (): string[] => {
  const hours = [];

  for (let i = 0; i < 24; i++) {
    const hora = i < 10 ? '0' + i : i;
    hours.push(hora + ':00:00');
    hours.push(hora + ':30:00');
  }

  return hours;
};

export const removeSecondsFromCompleteHour = (time: string): string => {
  const split = time.split(':');
  return `${split[0]}:${split[1]}`;
};

export const getDateTimeAccordingToTimeZone = ({
  date,
  zone,
}: {
  date: string;
  zone: string;
}): string => {
  return DateTime.fromSQL(date, { zone }).toString();
};

export const getFormattedWeekdayDate = (
  date: string | Date | null | undefined,
  format?: string
): string => {
  if (!date) return '';

  let dateTime;
  if (date instanceof Date) {
    if (isNaN(date.getTime())) return '';
    dateTime = DateTime.fromJSDate(date);
  } else {
    dateTime = date.includes('T')
      ? DateTime.fromISO(date)
      : DateTime.fromSQL(date);
  }

  if (!dateTime.isValid) return '';

  const luxonNumericDay = dateTime.weekday;
  const numericDay = luxonNumericDay % 7;
  const daysOfWeek = getDaysOfWeek(i18next.t);
  const dayLabel =
    daysOfWeek.find((d) => d.numericDay === numericDay)?.label ?? '';

  const dayMonthFormat = format || getDayMonthDateFormat();
  let formattedDate = dateTime.toFormat(dayMonthFormat);
  formattedDate =
    formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

  return `${dayLabel} ${formattedDate}`;
};

export const getCurrentWeekNumber = (): WeekNumbers => {
  return DateTime.local().weekNumber;
};

export const formattedDate = (date: string, timezone: string): string =>
  getFormattedWeekdayDate(
    getDateTimeAccordingToTimeZone({
      date,
      zone: timezone,
    }),
    getDayMonthDateFormat()
  );

export const getDaysOfWeek = (t: (key: string) => string): DayOfWeek[] => [
  { value: 'monday', label: t('common.monday'), numericDay: 1 },
  { value: 'tuesday', label: t('common.tuesday'), numericDay: 2 },
  { value: 'wednesday', label: t('common.wednesday'), numericDay: 3 },
  { value: 'thursday', label: t('common.thursday'), numericDay: 4 },
  { value: 'friday', label: t('common.friday'), numericDay: 5 },
  { value: 'saturday', label: t('common.saturday'), numericDay: 6 },
  { value: 'sunday', label: t('common.sunday'), numericDay: 0 },
];

const parseDateTime = (
  dateString?: string | undefined,
  timezone?: string | undefined
): DateTime | null => {
  if (!dateString || !timezone) {
    return null;
  }

  const parsedDateTime = DateTime.fromSQL(dateString, { zone: timezone });

  return parsedDateTime.isValid ? parsedDateTime : null;
};

const formatTime = ({
  dateTime,
  hour12 = false,
  locale,
  suffix24Hour = 'h',
  timezone,
}: {
  dateTime: string | Date;
  hour12?: boolean;
  locale?: string;
  suffix24Hour?: string;
  timezone?: string;
}): string => {
  if (!dateTime) {
    return '';
  }

  let dateTimeObj: DateTime;

  if (dateTime instanceof Date) {
    dateTimeObj = DateTime.fromJSDate(dateTime);
  } else {
    if (dateTime.includes('T') || dateTime.includes(' ')) {
      dateTimeObj = dateTime.includes('T')
        ? DateTime.fromISO(dateTime)
        : DateTime.fromSQL(dateTime);
    } else {
      const timeParts = dateTime.split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1] || '0', 10);

      if (isNaN(hours) || isNaN(minutes)) {
        return '';
      }

      dateTimeObj = DateTime.fromObject({
        hour: hours,
        minute: minutes,
      });
    }
  }

  if (!dateTimeObj.isValid) {
    return '';
  }

  if (timezone) {
    dateTimeObj = dateTimeObj.setZone(timezone);
  }

  if (locale) {
    dateTimeObj = dateTimeObj.setLocale(locale);
  }

  if (hour12) {
    return dateTimeObj
      .toFormat('h:mm a')
      .replace('AM', 'a. m.')
      .replace('PM', 'p. m.');
  }

  return dateTimeObj.toFormat(
    `HH:mm${suffix24Hour ? " '" + suffix24Hour + "'" : ''}`
  );
};

export { DEFAULT_TIME, formatTime, parseDateTime };
